import { useRotationStore } from '../../../context/RotationContext';
import { PortCall } from '../../../types/ViewModel/PortCall';
import { Rotation } from '../../../types/ViewModel/Rotation';
import { RotationBM } from '../../../types/BusinessModel/RotationBM';
import { Port } from '../../../types/ViewModel/Port';
import { CargoListItemBM } from '../../../types/BusinessModel/CargoListItemBM';
import { CargoListItem } from '../../../types/ViewModel/CargoListItem';
import { ReportLocationEnum } from '../../../enums/reportLocationEnum';
import { ChemicalRobPortCall } from '../../../types/ViewModel/TankCleaning/ROB/ChemicalRobPortCall';
import { ChemicalOrderPortCall } from '../../../types/ViewModel/TankCleaning/Order/ChemicalOrderPortCall';
import { newPortCall, newPortCallFromCode, newPortCallFromCodeExcludeMovedAndDeleted, updateBerthCallInPortCluster } from './PortCallFactory';

export default () => {
    const { setPortInPortClusterList } = useRotationStore();

    const updateVoyageNrForPortCluster = (): string => {
        return '';
    };

    const newRotation = (rotationBM: RotationBM | null): Rotation | null => {
        const portList: Port[] = [];
        if (!rotationBM) {
            return null;
        }

        const portCallArray: PortCall[] = [];
        if (rotationBM.portCalls && rotationBM.portCalls.length > 0) {
            let previousPortCall: PortCall;

            rotationBM.portCalls.forEach((portCall) => {
                const _newPortCall = newPortCall(portCall, 0);
                if (_newPortCall) {
                    // Do not include if same port cluster
                    if (!portCall.isAlreadyIncludedInAPortCluster) {
                        portCallArray.push(_newPortCall);
                        previousPortCall = _newPortCall;
                        if (portCall.portClusterName) {
                            previousPortCall.isInPortCluster = true;
                            previousPortCall.portClusterInitialPortId = portCall.port.portId;
                            previousPortCall.portClusterInitialPortName = portCall.port.portName;
                            updateBerthCallInPortCluster(_newPortCall.port?.portId, _newPortCall.port?.portName, previousPortCall.berthCallArray);
                        }
                    } else {
                        if (previousPortCall) {
                            previousPortCall.isInPortCluster = true;
                            if (previousPortCall.voyageNumber2 && !portCall.turnaroundVoyageNumber) {
                                // Do nothing
                            } else if (!previousPortCall.voyageNumber2 && portCall.turnaroundVoyageNumber) {
                                previousPortCall.voyageNumberPresentationString =
                                    previousPortCall.voyageNumber1 + '/' + portCall.turnaroundVoyageNumber;
                                previousPortCall.voyageNumber2 = portCall.turnaroundVoyageNumber;
                            } else if (previousPortCall.voyageNumber2 && portCall.turnaroundVoyageNumber) {
                                previousPortCall.voyageNumberPresentationString =
                                    previousPortCall.voyageNumber1 + '/' + portCall.turnaroundVoyageNumber;
                                previousPortCall.voyageNumber2 = portCall.turnaroundVoyageNumber;
                            } else if (previousPortCall.voyageNumber1 !== portCall.voyageNumber) {
                                previousPortCall.voyageNumberPresentationString = previousPortCall.voyageNumber1 + '/' + portCall.voyageNumber;
                                previousPortCall.voyageNumber2 = portCall.voyageNumber;
                            }

                            const addedBerthCalls = _newPortCall.berthCallArray;
                            if (addedBerthCalls?.length > 0) {
                                previousPortCall.berthCallArray = previousPortCall.portCall.berthCallArray?.concat(addedBerthCalls);
                                addedBerthCalls?.forEach((bc) => {
                                    bc.portClusterInitialPortId = _newPortCall.port?.portId ?? null;
                                    bc.portClusterInitialPortName = _newPortCall.port?.portName ?? null;
                                    bc.portCall = previousPortCall;
                                    bc.name = _newPortCall.port?.portName + ' - ' + bc.name;
                                    bc.cargoOperationArray?.forEach((co) => {
                                        co.portCall = previousPortCall;
                                    });
                                    bc.imosPortFunction = portCall.imosPortFunction;
                                });
                            }
                            // Add port to PortInPortClusterList
                            if (_newPortCall.port) {
                                const port = portList.find((o) => o.portId === _newPortCall.port.portId);
                                if (!port) {
                                    portList.push(_newPortCall.port);
                                }
                            }
                        }
                    }
                }
            });
        }
        setPortInPortClusterList(portList);
        let lastUpdatedUtc: Date | null = null;

        if (rotationBM.lastUpdatedUtc) {
            lastUpdatedUtc = new Date(rotationBM.lastUpdatedUtc);
        }
        return new Rotation(
            portCallArray,
            lastUpdatedUtc,
            rotationBM.stowagePlanName,
            rotationBM.lastImosSynchronizedUtc,
            rotationBM.lastImosUpdatedUtc
        );
    };

    const newRotationFromCode = (rotation: Rotation): Rotation => {
        const portCallArray: PortCall[] = [];
        if (rotation && rotation.portCallArray && rotation.portCallArray.length > 0) {
            rotation.portCallArray.forEach((pc) => {
                portCallArray.push(newPortCallFromCode(pc));
            });
        }
        return new Rotation(
            portCallArray,
            rotation.lastUpdatedUtc,
            rotation.stowagePlanName,
            rotation.lastImosSynchronizedUtc,
            rotation.lastImosUpdatedUtc
        );
    };

    const updateRotationWithConsumables = (
        rotation: Rotation,
        chemicalRobForPortCallList: ChemicalRobPortCall[],
        chemicalOrderForPortCallList: ChemicalOrderPortCall[]
    ) => {
        if (rotation && chemicalRobForPortCallList && chemicalOrderForPortCallList) {
            chemicalRobForPortCallList?.forEach((c) => {
                if (rotation.portCallArray.find((p) => c.portCallId === p.portCallId && p.reportLocation === ReportLocationEnum.Port)) {
                    const portCall = rotation.portCallArray.find(
                        (p) => c.portCallId === p.portCallId && p.reportLocation === ReportLocationEnum.Port
                    );
                    portCall.chemicalRobForPortCall = chemicalRobForPortCallList.find((c) => c.portCallId === portCall.portCallId);
                }
            });

            chemicalOrderForPortCallList?.forEach((c) => {
                if (
                    rotation.portCallArray.filter((p) => c.deliveryPortCallId === p.portCallId && p.reportLocation === ReportLocationEnum.Port)
                        .length > 0
                ) {
                    const portCall = rotation.portCallArray.find(
                        (p) => c.deliveryPortCallId === p.portCallId && p.reportLocation === ReportLocationEnum.Port
                    );
                    portCall.chemicalOrderForPortCallList = chemicalOrderForPortCallList.filter((c) => c.deliveryPortCallId === portCall.portCallId);
                }
            });
        }

        return rotation;
    };

    const newRotationFromCodeExcludeMovedAndDeleted = (rotation: Rotation): Rotation => {
        const portCallArray: PortCall[] = [];
        if (rotation && rotation.portCallArray && rotation.portCallArray.length > 0) {
            rotation.portCallArray.forEach((pc) => {
                if (!pc.isDeleted && !pc.isMoved) {
                    portCallArray.push(newPortCallFromCodeExcludeMovedAndDeleted(pc));
                }
            });
        }
        return new Rotation(
            portCallArray,
            rotation.lastUpdatedUtc,
            rotation.stowagePlanName,
            rotation.lastImosSynchronizedUtc,
            rotation.lastImosUpdatedUtc
        );
    };

    const getLastUpdatedForRotationAnyOnly = (rotation: RotationBM) => {
        return rotation ? new Date(rotation.lastUpdatedUtc) : null;
    };

    const mapCargoListItem = (cargoListItem: CargoListItemBM): CargoListItem => {
        return {
            id: cargoListItem.id,
            externalId: cargoListItem.externalId,
            cargoNumber: cargoListItem.number,
            chartererName: cargoListItem.chartererName,
            tradeName: cargoListItem.product?.tradeName,
        };
    };

    const mapCargoList = (cargoList: CargoListItemBM[]): CargoListItem[] => {
        return cargoList ? cargoList.map((cgo) => mapCargoListItem(cgo)) : [];
    };

    return {
        updateVoyageNrForPortCluster,
        newRotation,
        newRotationFromCode,
        newRotationFromCodeExcludeMovedAndDeleted,
        updateRotationWithConsumables,
        getLastUpdatedForRotationAnyOnly,
        mapCargoList,
    };
};
